// In-place edit panel for gallery detail view. Four actions:
//   1. Re-run AI (description + BG removal + cutout) on the existing photo
//   2. Replace photo (uploads new file, then re-runs AI)
//   3. Replace cutout directly (for when you processed one manually in Preview/PS)
//   4. Delete the entry permanently
//
// Works on any order (current or historical). All writes are staff-gated
// by firestore.rules.

import { useRef, useState } from "react";
import { doc, serverTimestamp, Timestamp, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, storage, auth } from "./firebase";
import { trashOrder, TRASH_RETENTION_DAYS } from "./utils/trash";
import PreCropTool from "./components/PreCropTool";
import CutoutTouchUp from "./components/CutoutTouchUp";

import { STAFF_THEME } from "./theme/staff";
const BRAND = { ...STAFF_THEME, red: "#c94444" };

const MAX_EDGE = 1920;
const JPEG_QUALITY = 0.85;

async function compressToJpeg(file) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = URL.createObjectURL(file);
  });
  const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  URL.revokeObjectURL(img.src);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", JPEG_QUALITY));
}

// Delete is intentionally NOT here — deletion lives only on /archive (Shift
// Archive view). Keeping gallery read+edit prevents accidental data loss
// during casual browsing.
export default function GalleryEditPanel({ order, onClose, onDeleted }) {
  const [busy, setBusy]   = useState(null); // null | "rerun" | "photo" | "cutout" | "precrop" | "touchup" | "trash"
  const [msg,  setMsg]    = useState(null); // { kind: "ok"|"err", text }
  const [showPreCrop, setShowPreCrop] = useState(false);
  const [showTouchUp, setShowTouchUp] = useState(false);
  const photoInputRef  = useRef();

  const showMsg = (kind, text, autoClear = true) => {
    setMsg({ kind, text });
    if (autoClear) setTimeout(() => setMsg(null), 3500);
  };

  const tokenPath = order.tokenId || order.id; // tokenId for older/current orders; order.id as safe fallback

  const rerunAi = async () => {
    if (busy) return;
    if (!order.photo_url) { showMsg("err", "No photo on this order to process."); return; }
    if (!window.confirm("Re-run AI description on this photo? (Cutout untouched.) Takes 5-15s.")) return;
    setBusy("rerun"); setMsg(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "generatePancakeDescription");
      await fn({
        orderId:       order.id,
        photoUrl:      order.photo_url,
        tokenId:       order.tokenId || order.id,
        subjectName:   order.subjectName || null,
        recipientName: order.recipientName || null,
        notes:         order.notes || null,
        skipCutout:    true,
      });
      showMsg("ok", "✓ New description generated.");
    } catch (err) {
      console.error(err);
      showMsg("err", err.message || "Re-run failed.", false);
    } finally {
      setBusy(null);
    }
  };

  const replacePhoto = async (file) => {
    if (!file || busy) return;
    if (!window.confirm("Replace the photo on this entry and re-run AI description + cutout?")) return;
    setBusy("photo"); setMsg(null);
    try {
      const blob = await compressToJpeg(file);
      const path = `orders/${tokenPath}/${Date.now()}_replaced.jpg`;
      const r = storageRef(storage, path);
      await uploadBytes(r, blob, { contentType: "image/jpeg" });
      const newUrl = await getDownloadURL(r);

      // Clear old cutout + thumbnail + AI output; they'll be regenerated
      await updateDoc(doc(db, "orders", order.id), {
        photo_url:           newUrl,
        photo_cutout_url:    null,
        photo_thumb_url:     null,
        cutoutThumbnailUrl:  null,
        thumbnailUrl:        null,
        ai_description:      null,
        ai_tags:             null,
        updatedAt:           serverTimestamp(),
      });

      const fn = httpsCallable(getFunctions(getApp()), "generatePancakeDescription");
      await fn({
        orderId:       order.id,
        photoUrl:      newUrl,
        tokenId:       tokenPath,
        subjectName:   order.subjectName || null,
        recipientName: order.recipientName || null,
        notes:         order.notes || null,
      });
      showMsg("ok", "✓ Photo replaced and AI re-run complete. Refresh to see changes.");
    } catch (err) {
      console.error(err);
      showMsg("err", err.message || "Replace failed.", false);
    } finally {
      setBusy(null);
    }
  };

  const handlePreCropConfirm = async (croppedBlob) => {
    setShowPreCrop(false);
    if (!croppedBlob || busy) return;
    setBusy("precrop"); setMsg(null);
    try {
      // Cropped blob already has compression; uploaded as-is, then we kick off
      // the same AI + cutout pipeline used by Replace photo.
      const path = `orders/${tokenPath}/${Date.now()}_precrop.jpg`;
      const r = storageRef(storage, path);
      await uploadBytes(r, croppedBlob, { contentType: "image/jpeg" });
      const newUrl = await getDownloadURL(r);

      await updateDoc(doc(db, "orders", order.id), {
        photo_url:           newUrl,
        photo_cutout_url:    null,
        photo_thumb_url:     null,
        cutoutThumbnailUrl:  null,
        thumbnailUrl:        null,
        ai_description:      null,
        ai_tags:             null,
        updatedAt:           serverTimestamp(),
      });

      const fn = httpsCallable(getFunctions(getApp()), "generatePancakeDescription");
      await fn({
        orderId:       order.id,
        photoUrl:      newUrl,
        tokenId:       tokenPath,
        subjectName:   order.subjectName || null,
        recipientName: order.recipientName || null,
        notes:         order.notes || null,
      });
      showMsg("ok", "✓ Cropped and re-ran cutout. Refresh to see.");
    } catch (err) {
      console.error(err);
      showMsg("err", err.message || "Pre-crop failed.", false);
    } finally {
      setBusy(null);
    }
  };

  const handleTouchUpConfirm = async (cleanedPngBlob) => {
    setShowTouchUp(false);
    if (!cleanedPngBlob || busy) return;
    setBusy("touchup"); setMsg(null);
    try {
      const path = `orders/${tokenPath}/${Date.now()}_touchup.png`;
      const r = storageRef(storage, path);
      await uploadBytes(r, cleanedPngBlob, { contentType: "image/png" });
      const newUrl = await getDownloadURL(r);
      await updateDoc(doc(db, "orders", order.id), {
        photo_cutout_url:    newUrl,
        photo_thumb_url:     null,
        cutoutThumbnailUrl:  null,
        thumbnailUrl:        null,
        updatedAt:           serverTimestamp(),
      });
      showMsg("ok", "✓ Touch-up saved. Refresh to see.");
    } catch (err) {
      console.error(err);
      showMsg("err", err.message || "Touch-up save failed.", false);
    } finally {
      setBusy(null);
    }
  };

  const moveToTrash = async () => {
    if (busy) return;
    const name = order.subjectName || order.recipientName || order.customerName || "this pancake";
    if (!window.confirm(`Move "${name}" to trash? It'll be recoverable for ${TRASH_RETENTION_DAYS} days from the Shifts tab's Trash section.`)) return;
    setBusy("trash"); setMsg(null);
    try {
      await trashOrder(order.id, auth.currentUser?.email);
      showMsg("ok", "✓ Moved to trash. Recoverable from the Trash section in Shifts.");
      // Close + navigate away after a short delay so the user sees confirmation.
      setTimeout(() => { onDeleted?.(); }, 700);
    } catch (err) {
      console.error(err);
      showMsg("err", err.message || "Trash failed.", false);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{
      background: BRAND.panel,
      border: `1px solid ${BRAND.border}`,
      borderRadius: "10px",
      padding: "0.85rem 0.95rem",
      marginTop: "0.75rem",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.7rem" }}>
        <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.05em" }}>
          ✏️ EDIT ENTRY
        </div>
        <button type="button" onClick={onClose}
          style={{ background: "transparent", color: BRAND.muted, border: "none", fontSize: "1.15rem", cursor: "pointer", lineHeight: 1 }}>
          ×
        </button>
      </div>

      {msg && (
        <div style={{
          background: msg.kind === "ok" ? "rgba(112,190,68,0.15)" : "rgba(201,68,68,0.15)",
          border: `1px solid ${msg.kind === "ok" ? BRAND.green : BRAND.red}`,
          color: msg.kind === "ok" ? BRAND.green : "#ff8a8a",
          borderRadius: "8px",
          padding: "0.55rem 0.7rem",
          marginBottom: "0.6rem",
          fontSize: "0.82rem",
        }}>
          {msg.text}
        </div>
      )}

      <PublicGalleryToggle order={order} />
      <HiddenToggle order={order} />
      <DateEditor order={order} />

      <Action
        label="🔍 Preview as customer"
        hint="Opens the full customer keepsake page for this pancake in a new tab. No ticket required."
        onClick={() => window.open(`/preview-keepsake/${order.id}`, "_blank")}
      />

      <Action
        label="📝 Re-run AI description"
        hint="Regenerate the alt-text + tags from the existing photo. Cutout untouched. 5-15s."
        busy={busy === "rerun"}
        disabled={!!busy}
        onClick={rerunAi}
      />

      <Action
        label="📐 Pre-crop & re-run cutout"
        hint="Crop tighter to the pancake, then re-run AI + cutout. Best fix for griddle-edge noise. 30-60s."
        busy={busy === "precrop"}
        disabled={!!busy || !order.photo_url}
        onClick={() => setShowPreCrop(true)}
      />

      <Action
        label="🪄 Touch up cutout"
        hint="Brush in / brush out alpha by hand. Best fix for missing details (dark clothing, broken linework)."
        busy={busy === "touchup"}
        disabled={!!busy || !order.photo_cutout_url}
        onClick={() => setShowTouchUp(true)}
      />

      <Action
        label="📷 Replace photo"
        hint="Upload a new photo, then re-run AI + cutout. 30-60s."
        busy={busy === "photo"}
        disabled={!!busy}
        onClick={() => photoInputRef.current?.click()}
      />
      <input ref={photoInputRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) replacePhoto(f); e.target.value = ""; }} />

      {/* Trash — soft-delete, recoverable for 30 days from the Trash bin
          on the Shifts tab. Styled danger-red to distance it from the
          constructive editing actions above. */}
      <button
        type="button"
        onClick={moveToTrash}
        disabled={!!busy}
        style={{
          display: "block", width: "100%", textAlign: "left",
          background: "rgba(201,68,68,0.1)",
          border: `1px solid rgba(201,68,68,0.4)`,
          borderRadius: "8px",
          padding: "0.6rem 0.8rem",
          marginTop: "0.7rem",
          cursor: busy ? "default" : "pointer",
          opacity: busy ? 0.5 : 1,
        }}
      >
        <div style={{ color: "#ff8a8a", fontWeight: 700, fontSize: "0.88rem" }}>
          {busy === "trash" ? "Moving to trash…" : "🗑 Move to trash"}
        </div>
        <div style={{ color: "rgba(255,138,138,0.6)", fontSize: "0.72rem", marginTop: "0.15rem" }}>
          Recoverable for {TRASH_RETENTION_DAYS} days from the Trash section in Shifts.
        </div>
      </button>

      {showPreCrop && order.photo_url && (
        <PreCropTool
          src={order.photo_url}
          title="Crop to the pancake"
          subtitle="Tight crop = cleaner cutout. Drag handles to size, drag inside to move."
          onConfirm={handlePreCropConfirm}
          onCancel={() => setShowPreCrop(false)}
        />
      )}

      {showTouchUp && order.photo_cutout_url && (
        <CutoutTouchUp
          cutoutSrc={order.photo_cutout_url}
          originalSrc={order.photo_url}
          onConfirm={handleTouchUpConfirm}
          onCancel={() => setShowTouchUp(false)}
        />
      )}
    </div>
  );
}

// Date correction — historical uploads often get the wrong date when better
// info surfaces later. Edits completedAt (the primary gallery/meditations
// sort field) and mirrors to deliveredAt when that field was already set,
// so the pair stays consistent. Photographed-at is left alone since it has
// semantic meaning (when the shutter actually clicked on real orders).
function DateEditor({ order }) {
  const currentTs = order.completedAt?.toDate?.()
    || order.photographedAt?.toDate?.()
    || order.createdAt?.toDate?.()
    || null;
  const iso = currentTs
    ? `${currentTs.getFullYear()}-${String(currentTs.getMonth() + 1).padStart(2, "0")}-${String(currentTs.getDate()).padStart(2, "0")}`
    : "";
  const [value, setValue] = useState(iso);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const dirty = value && value !== iso;

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true); setFeedback(null);
    try {
      const dt = new Date(value + "T12:00:00");
      if (isNaN(dt.getTime())) throw new Error("Invalid date");
      const ts = Timestamp.fromDate(dt);
      const updates = { completedAt: ts, updatedAt: serverTimestamp() };
      if (order.deliveredAt) updates.deliveredAt = ts;
      await updateDoc(doc(db, "orders", order.id), updates);
      setFeedback({ ok: true, text: "✓ Date updated" });
      setTimeout(() => setFeedback(null), 2500);
    } catch (e) {
      setFeedback({ ok: false, text: e.message || "Update failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.15)",
      borderRadius: "8px",
      padding: "0.6rem 0.8rem",
      marginBottom: "0.45rem",
    }}>
      <div style={{ color: "var(--jop-cream, #ffdaad)", fontWeight: 700, fontSize: "0.88rem", marginBottom: "0.1rem" }}>
        🗓 Date
      </div>
      <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.72rem", marginBottom: "0.45rem" }}>
        Correct the date when you have better info. Updates gallery sort + meditations timeline.
      </div>
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
        <input
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          style={{
            flex: "1 1 160px",
            background: "rgba(0,0,0,0.3)",
            color: "var(--jop-cream, #ffdaad)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "6px",
            padding: "0.4rem 0.55rem",
            fontSize: "0.85rem",
            fontFamily: "inherit",
          }}
        />
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          style={{
            background: dirty ? BRAND.gold : "rgba(255,255,255,0.08)",
            color: dirty ? "#0a0f1c" : "rgba(255,255,255,0.35)",
            border: "none",
            borderRadius: "6px",
            padding: "0.45rem 0.85rem",
            fontSize: "0.82rem",
            fontWeight: 700,
            cursor: dirty && !saving ? "pointer" : "default",
          }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {feedback && (
        <div style={{
          marginTop: "0.4rem",
          color: feedback.ok ? BRAND.green : "#ff8a8a",
          fontSize: "0.78rem",
        }}>
          {feedback.text}
        </div>
      )}
    </div>
  );
}

function PublicGalleryToggle({ order }) {
  const [on, setOn] = useState(!!order.publicGallery);
  const [busy, setBusy] = useState(false);

  const flip = async () => {
    if (busy) return;
    const next = !on;
    setBusy(true);
    setOn(next); // optimistic
    try {
      await updateDoc(doc(db, "orders", order.id), {
        publicGallery: next,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      setOn(!next); // revert
      alert("Toggle failed: " + err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={flip}
      disabled={busy}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        width: "100%", textAlign: "left",
        background: on ? "rgba(252,179,31,0.18)" : "rgba(255,255,255,0.04)",
        border: `1px solid ${on ? BRAND.gold : "rgba(255,255,255,0.15)"}`,
        borderRadius: "8px",
        padding: "0.6rem 0.8rem",
        marginBottom: "0.45rem",
        cursor: busy ? "default" : "pointer",
      }}
    >
      <div>
        <div style={{ color: on ? BRAND.gold : "var(--jop-cream, #ffdaad)", fontWeight: 700, fontSize: "0.88rem" }}>
          {on ? "⭐ Showing on public website" : "☆ Show on public website"}
        </div>
        <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.72rem", marginTop: "0.15rem" }}>
          Toggles inclusion in the "Fresh off the griddle" gallery on /site.
        </div>
      </div>
      <div style={{
        width: "36px", height: "20px", borderRadius: "999px",
        background: on ? BRAND.gold : "rgba(255,255,255,0.18)",
        position: "relative", flexShrink: 0,
        transition: "background 0.18s ease",
      }}>
        <div style={{
          position: "absolute", top: "2px", left: on ? "18px" : "2px",
          width: "16px", height: "16px", borderRadius: "50%",
          background: on ? BRAND.dark : "#fff",
          transition: "left 0.18s ease",
        }} />
      </div>
    </button>
  );
}

function HiddenToggle({ order }) {
  const [on, setOn] = useState(!!order.hidden);
  const [busy, setBusy] = useState(false);

  const flip = async () => {
    if (busy) return;
    const next = !on;
    setBusy(true);
    setOn(next);
    try {
      await updateDoc(doc(db, "orders", order.id), {
        hidden: next,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      setOn(!next);
      alert("Toggle failed: " + err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={flip}
      disabled={busy}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        width: "100%", textAlign: "left",
        background: on ? "rgba(155,89,182,0.15)" : "rgba(255,255,255,0.04)",
        border: `1px solid ${on ? "#9b59b6" : "rgba(255,255,255,0.15)"}`,
        borderRadius: "8px",
        padding: "0.6rem 0.8rem",
        marginBottom: "0.45rem",
        cursor: busy ? "default" : "pointer",
      }}
    >
      <div>
        <div style={{ color: on ? "#c39bd3" : "var(--jop-cream, #ffdaad)", fontWeight: 700, fontSize: "0.88rem" }}>
          {on ? "👁‍🗨 Hidden from gallery" : "👁 Visible in gallery"}
        </div>
        <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.72rem", marginTop: "0.15rem" }}>
          Hide this pancake from the staff gallery without deleting data. Still visible in /archive.
        </div>
      </div>
      <div style={{
        width: "36px", height: "20px", borderRadius: "999px",
        background: on ? "#9b59b6" : "rgba(255,255,255,0.18)",
        position: "relative", flexShrink: 0,
        transition: "background 0.18s ease",
      }}>
        <div style={{
          position: "absolute", top: "2px", left: on ? "18px" : "2px",
          width: "16px", height: "16px", borderRadius: "50%",
          background: "#fff",
          transition: "left 0.18s ease",
        }} />
      </div>
    </button>
  );
}

function Action({ label, hint, busy, disabled, onClick }) {
  const bg = "rgba(252,179,31,0.08)";
  const bd = "rgba(252,179,31,0.35)";
  const fg = BRAND.gold;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "block", width: "100%",
        textAlign: "left",
        background: bg,
        border: `1px solid ${bd}`,
        borderRadius: "8px",
        padding: "0.6rem 0.8rem",
        marginBottom: "0.45rem",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled && !busy ? 0.5 : 1,
      }}
    >
      <div style={{ color: fg, fontWeight: 700, fontSize: "0.88rem" }}>
        {busy ? "Working…" : label}
      </div>
      <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.72rem", marginTop: "0.15rem" }}>
        {hint}
      </div>
    </button>
  );
}
